/**
 * Campaign report PDF — async job creation + polling.
 *
 *   POST /campaign/:id/report-pdf            → { jobId }
 *   GET  /campaign/:id/report-pdf/:jobId     → status poll
 *   GET  /campaign/:id/report-pdf/:jobId/download → PDF stream when done
 */

import fetchJson from "@/lib/fetchJson";
import useSWR from "swr";

export type CampaignReportJobStatus =
  | "pending"
  | "running"
  | "done"
  | "failed";

export type CampaignReportJobPhase =
  | "rendering"
  | "downloading"
  | "compressing";

export interface CampaignReportJob {
  jobId: string;
  campaignId: number;
  status: CampaignReportJobStatus;
  phase: CampaignReportJobPhase | string | null;
  error: string | null;
  fileName: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  runningForMs: number | null;
}

export interface CreateCampaignReportInput {
  campaignId: number;
  query: URLSearchParams;
  logo: string | null;
}

export const TERMINAL_REPORT_STATUSES: ReadonlySet<CampaignReportJobStatus> =
  new Set<CampaignReportJobStatus>(["done", "failed"]);

export function isTerminalReportStatus(
  status: CampaignReportJobStatus,
): boolean {
  return TERMINAL_REPORT_STATUSES.has(status);
}

export async function createCampaignReport(
  input: CreateCampaignReportInput,
): Promise<{ jobId: string }> {
  const qs = input.query.toString();
  const body = JSON.stringify({ image: input.logo ?? null });
  const res = await fetchJson(
    `/campaign/${input.campaignId}/report-pdf${qs ? `?${qs}` : ""}`,
    { method: "POST", body, credentials: "include" },
  );
  if (!res || typeof res !== "object" || typeof (res as { jobId?: unknown }).jobId !== "string") {
    throw new Error("Failed to start report generation");
  }
  return { jobId: (res as { jobId: string }).jobId };
}

export function useCampaignReportJob(
  campaignId: number | undefined,
  jobId: string | null,
) {
  const key =
    campaignId && jobId ? `/campaign/${campaignId}/report-pdf/${jobId}` : null;
  return useSWR<CampaignReportJob>(key, fetchJson, {
    refreshInterval: (latest) => {
      if (!latest) return 2000;
      return isTerminalReportStatus(latest.status) ? 0 : 2000;
    },
    revalidateOnFocus: false,
    dedupingInterval: 1000,
    shouldRetryOnError: false,
  });
}

export function getReportDownloadUrl(
  campaignId: number,
  jobId: string,
  filename?: string,
): string {
  const isDevProxy =
    typeof process !== "undefined" &&
    process.env.NODE_ENV === "development" &&
    process.env.NEXT_PUBLIC_PROXY !== "no";
  const prefix = isDevProxy ? "/api/proxy/backend" : "/backend";
  const qs = filename ? `?filename=${encodeURIComponent(filename)}` : "";
  return `${prefix}/campaign/${campaignId}/report-pdf/${jobId}/download${qs}`;
}
